import { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { en } from './en';
import { ru } from './ru';
import { LOCALES } from './types';
import type { Locale, MessageParams, Messages, PluralKey, SimpleKey } from './types';

/**
 * Dictionaries as the runtime sees them: partial, so a hand-edited file that
 * lost a key falls back to English instead of showing the key itself. The
 * exported `ru` is still typed `Messages`, so the compiler catches the gap
 * first — this is the second line of defence, not the first.
 */
const DICTIONARIES: Record<Locale, Partial<Messages>> = { en, ru };

const STORAGE_KEY = 'gdsgate.locale';

export interface I18n {
  locale: Locale;
  locales: readonly Locale[];
  setLocale: (locale: Locale) => void;

  /** Look up a message and substitute `{placeholders}`. */
  t: (key: SimpleKey, params?: MessageParams) => string;
  /** Look up a counted message. `{count}` is substituted for you. */
  plural: (key: PluralKey, count: number, params?: MessageParams) => string;

  formatNumber: (value: number) => string;
  formatDate: (value: string | Date) => string;
  /**
   * A moment, to the minute.
   *
   * Its own formatter because some answers are about an instant rather than a
   * day: "the registry was read at" is not the same statement as "on the 29th",
   * and a figure counted as of a moment has to show which moment.
   */
  formatDateTime: (value: string | Date) => string;
  /** "3 days ago" / "in 2 hours", written by Intl rather than glued together. */
  formatRelative: (value: string | Date) => string;
  /**
   * A length of time, for saying how long a wait is.
   *
   * Its own formatter rather than `formatRelative`: "in 15 minutes" is a moment
   * and "15 minutes" is a duration, and the sentence a refusal needs is the
   * second one.
   */
  formatDuration: (seconds: number) => string;
}

export const I18nContext = createContext<I18n | null>(null);

/** Replace `{name}` with a value. Numbers are formatted for the locale. */
function interpolate(template: string, params: MessageParams | undefined, locale: Locale): string {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (whole, name: string) => {
    const value = params[name];
    if (value === undefined) return whole;
    return typeof value === 'number' ? new Intl.NumberFormat(locale).format(value) : value;
  });
}

/**
 * English, always.
 *
 * The console ships in one language: the switch is not mounted anywhere, so a
 * locale picked from the browser or left over in storage would be one nobody
 * asked for and nobody can change back. Neither is read here for that reason —
 * detection that cannot be overridden is worse than no detection.
 *
 * The machinery underneath is untouched and works: `setLocale` still switches,
 * `ru.ts` is still translated and still checked by tests. Showing the switch
 * again is mounting `LanguageSwitch` and reading storage here.
 */
function detectLocale(): Locale {
  return 'en';
}

const RELATIVE_UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ['year', 365 * 24 * 3600],
  ['month', 30 * 24 * 3600],
  ['day', 24 * 3600],
  ['hour', 3600],
  ['minute', 60],
  ['second', 1],
];

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(detectLocale);

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const setLocale = useCallback((next: Locale) => {
    localStorage.setItem(STORAGE_KEY, next);
    setLocaleState(next);
  }, []);

  const value = useMemo<I18n>(() => {
    const dictionary = DICTIONARIES[locale];
    const pluralRules = new Intl.PluralRules(locale);
    const numberFormat = new Intl.NumberFormat(locale);
    const dateFormat = new Intl.DateTimeFormat(locale, { dateStyle: 'medium' });
    const dateTimeFormat = new Intl.DateTimeFormat(locale, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
    const relativeFormat = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });

    const t = (key: SimpleKey, params?: MessageParams): string => {
      const entry = dictionary[key] ?? en[key];
      return interpolate(entry, params, locale);
    };

    const plural = (key: PluralKey, count: number, params?: MessageParams): string => {
      const forms = (dictionary[key] ?? en[key]) as Record<string, string | undefined>;
      const template = forms[pluralRules.select(count)] ?? forms['other'] ?? '';
      return interpolate(template, { count, ...params }, locale);
    };

    const formatRelative = (input: string | Date): string => {
      const then = typeof input === 'string' ? new Date(input) : input;
      const seconds = Math.round((then.getTime() - Date.now()) / 1000);
      const magnitude = Math.abs(seconds);
      for (const [unit, size] of RELATIVE_UNITS) {
        if (magnitude >= size || unit === 'second') {
          return relativeFormat.format(Math.round(seconds / size), unit);
        }
      }
      return relativeFormat.format(0, 'second');
    };

    const formatDuration = (seconds: number): string => {
      const [unit, size]: [Intl.NumberFormatOptions['unit'], number] =
        seconds >= 3600 ? ['hour', 3600] : seconds >= 90 ? ['minute', 60] : ['second', 1];
      const value = Math.max(1, Math.round(seconds / size));
      return new Intl.NumberFormat(locale, {
        style: 'unit',
        unit,
        unitDisplay: 'long',
        maximumFractionDigits: 0,
      }).format(value);
    };

    return {
      locale,
      locales: LOCALES,
      setLocale,
      t,
      plural,
      formatNumber: (n) => numberFormat.format(n),
      formatDate: (d) => dateFormat.format(typeof d === 'string' ? new Date(d) : d),
      formatDateTime: (d) => dateTimeFormat.format(typeof d === 'string' ? new Date(d) : d),
      formatRelative,
      formatDuration,
    };
  }, [locale, setLocale]);

  return <I18nContext value={value}>{children}</I18nContext>;
}
